// UC91-03 / UC91-08 / UC91-09 — line chart with a distinct color and marker per
// series, a dashed segment for extrapolated future buckets and a hollow marker where
// a bucket with no report of its own carries the last level reported. Rendered as
// inline SVG using AGE color tokens (no charting dependency); the colors come from
// the backend per series so the chart and table stay color-coordinated. CF-66: at
// most CHART_SERIES_LIMIT series are drawn, and the legend, which lists them all,
// is where the reader chooses which (docs/shape-uc-91-series-and-movements.md §2 D5).
import { useTranslate } from '@tolgee/react';
import { MudCheckbox } from '@egov-moldova/mud-react';
import { splitMedicineLabel } from '@simsm/shared-types';
import { formatNumber } from '@simsm/ui-shared';
import { SeriesGlyph, SeriesMarker } from './SeriesMarker';
import {
  CHART_SERIES_LIMIT,
  markerOf,
  placeOf,
  pointKind,
  seriesKey,
  type MarkerShape,
} from './stock-series';
import type { TimeseriesPoint, TimeseriesSeries } from './useStockTimeseries';

interface StockLineChartProps {
  buckets: string[];
  // Every series of the response, in order; only the `charted` ones are drawn.
  series: TimeseriesSeries[];
  charted: ReadonlySet<string>;
  onToggle: (key: string, shown: boolean) => void;
  // Under the regional and locality levels a medicine is one line per place, and
  // the legend names the place beside the medicine.
  grouped: boolean;
}

const WIDTH = 920;
const HEIGHT = 320;
const PADDING = { top: 16, right: 16, bottom: 36, left: 56 };

// A reported point is the series' marker, filled. A carried one is the same marker
// hollow on the solid line, as the wireframe draws "stoc reportat"
// (GraficStocuri.tsx, `p.reportat`); a projected one is the smaller hollow marker on
// the dashed segment. The two differ in size and in the line they sit on, never in
// colour alone.
function pointMark(point: TimeseriesPoint): { r: number; strokeWidth: number; ring: boolean } {
  const kind = pointKind(point);
  if (kind === 'projected') return { r: 3, strokeWidth: 1.5, ring: true };
  if (kind === 'carried') return { r: 4, strokeWidth: 2, ring: true };
  return { r: 4, strokeWidth: 0, ring: false };
}

function niceMax(value: number): number {
  if (value <= 0) return 10;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export function StockLineChart({
  buckets,
  series,
  charted,
  onToggle,
  grouped,
}: StockLineChartProps): JSX.Element {
  const { t } = useTranslate();
  const unassigned = t('stockChart.group.unassigned');
  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  // The drawn series take the markers in order, so the eight on the chart never
  // share one; a series not drawn has none, and its legend entry shows only its line.
  const shapeOf = new Map(
    series
      .map(seriesKey)
      .filter((key) => charted.has(key))
      .map((key, index) => [key, markerOf(index)] as const),
  );
  const marked = series.map((s) => ({ s, shape: shapeOf.get(seriesKey(s)) ?? null }));
  const drawn = marked.flatMap(({ s, shape }) => (shape ? [{ s, shape }] : []));
  const full = charted.size >= CHART_SERIES_LIMIT;

  const allValues = drawn.flatMap(({ s }) => s.points.map((p) => p.quantity));
  const kinds = new Set(drawn.flatMap(({ s }) => s.points.map(pointKind)));
  const maxValue = niceMax(Math.max(...allValues, 0));
  const bucketIndex = new Map(buckets.map((bucket, index) => [bucket, index]));

  const xFor = (index: number): number =>
    buckets.length <= 1
      ? PADDING.left + plotWidth / 2
      : PADDING.left + (index / (buckets.length - 1)) * plotWidth;
  const yFor = (value: number): number =>
    PADDING.top + plotHeight - (value / maxValue) * plotHeight;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
    value: Math.round(maxValue * fraction),
    y: PADDING.top + plotHeight - fraction * plotHeight,
  }));

  // Eight ticks at most, the last one included, but never the last two on top of
  // each other: a final tick closer than one step to its neighbour is dropped.
  const tickStep = Math.max(1, Math.ceil(buckets.length / 8));
  const axisLabels = buckets.filter((_, index) => {
    if (index === buckets.length - 1) return true;
    if (index % tickStep !== 0) return false;
    return buckets.length - 1 - index >= tickStep;
  });

  return (
    <figure className="simsm-stockchart" aria-label={t('stockChart.chart.aria_label')}>
      {/* eslint-disable-next-line no-restricted-syntax -- a data chart, not an icon: the stock series drawn over AGE tokens; MudIcon draws AGE's icon set only */}
      <svg
        className="simsm-stockchart__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={t('stockChart.chart.aria_label')}
        preserveAspectRatio="xMidYMid meet"
      >
        <title>{t('stockChart.chart.aria_label')}</title>
        {ticks.map((tick) => (
          <g key={`tick-${tick.value}`}>
            <line
              x1={PADDING.left}
              y1={tick.y}
              x2={WIDTH - PADDING.right}
              y2={tick.y}
              className="simsm-stockchart__gridline"
            />
            <text x={PADDING.left - 8} y={tick.y + 4} className="simsm-stockchart__ytick">
              {formatNumber(tick.value)}
            </text>
          </g>
        ))}

        {axisLabels.map((bucket) => {
          const index = bucketIndex.get(bucket) ?? 0;
          return (
            <text
              key={`xlabel-${bucket}`}
              x={xFor(index)}
              y={HEIGHT - 12}
              className="simsm-stockchart__xtick"
              textAnchor="middle"
            >
              {bucket}
            </text>
          );
        })}

        {drawn.map(({ s, shape }) => {
          const observed = s.points.filter((p) => !p.isProjected);
          const projected = s.points.filter((p) => p.isProjected);
          const lastObserved = observed[observed.length - 1];
          const observedPath = observed
            .map((p, idx) => {
              const x = xFor(bucketIndex.get(p.bucket) ?? idx);
              const y = yFor(p.quantity);
              return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');
          const projectedAnchor = lastObserved ? [lastObserved, ...projected] : projected;
          const projectedPath = projectedAnchor
            .map((p, idx) => {
              const x = xFor(bucketIndex.get(p.bucket) ?? idx);
              const y = yFor(p.quantity);
              return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');

          return (
            <g key={seriesKey(s)}>
              {observedPath ? (
                <path d={observedPath} fill="none" stroke={s.color} strokeWidth={2} />
              ) : null}
              {projected.length > 0 && projectedPath ? (
                <path
                  d={projectedPath}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                />
              ) : null}
              {s.points.map((p) => {
                const mark = pointMark(p);
                return (
                  <SeriesMarker
                    key={`${seriesKey(s)}-${p.bucket}`}
                    shape={shape}
                    x={xFor(bucketIndex.get(p.bucket) ?? 0)}
                    y={yFor(p.quantity)}
                    r={mark.r}
                    fill={mark.ring ? 'var(--color-background-base-default)' : s.color}
                    stroke={s.color}
                    strokeWidth={mark.strokeWidth}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* The wireframe's key under the plot (Stocuri.tsx, `leg-linie`, `leg-gol`): what
          a solid line, a dashed one and a ring mean. Only what is drawn is named; the
          solid line runs through the carried points too
          (docs/shape-uc-91-time-window.md §2). */}
      {kinds.size > 0 ? (
        <div className="simsm-stockchart__key">
          {kinds.has('reported') || kinds.has('carried') ? (
            <span className="simsm-stockchart__key-item">
              <span className="simsm-stockchart__key-line" aria-hidden="true" />
              {t('stockChart.legend.reported')}
            </span>
          ) : null}
          {kinds.has('projected') ? (
            <span className="simsm-stockchart__key-item">
              <span
                className="simsm-stockchart__key-line simsm-stockchart__key-line--dashed"
                aria-hidden="true"
              />
              {t('stockChart.legend.projected')}
            </span>
          ) : null}
          {kinds.has('carried') ? (
            <span className="simsm-stockchart__key-item">
              <span className="simsm-stockchart__key-ring" aria-hidden="true" />
              {t('stockChart.legend.carried')}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* CF-66: every series is listed with its marker, colour and name, and its box
          says whether it is drawn. Once CHART_SERIES_LIMIT are drawn, another can be
          ticked only after one is unticked (TC-UC91-03-04). */}
      <figcaption className="simsm-stockchart__legend">
        {marked.map(({ s, shape }) => (
          <LegendEntry
            key={`legend-${seriesKey(s)}`}
            series={s}
            shape={shape}
            shown={charted.has(seriesKey(s))}
            locked={full && !charted.has(seriesKey(s))}
            place={grouped ? placeOf(s, unassigned) : ''}
            noData={t('stockChart.legend.no_data')}
            onToggle={onToggle}
          />
        ))}
      </figcaption>
    </figure>
  );
}

interface LegendEntryProps {
  series: TimeseriesSeries;
  shape: MarkerShape | null;
  shown: boolean;
  locked: boolean;
  place: string;
  noData: string;
  onToggle: (key: string, shown: boolean) => void;
}

function LegendEntry({
  series,
  shape,
  shown,
  locked,
  place,
  noData,
  onToggle,
}: LegendEntryProps): JSX.Element {
  return (
    <MudCheckbox
      size="sm"
      checked={shown}
      disabled={locked}
      onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
        onToggle(seriesKey(series), e.detail.checked)
      }
    >
      <span slot="label" className="simsm-stockchart__legend-item">
        <SeriesGlyph shape={shape} color={series.color} />
        {/* The wireframe's legend is `nume: m.denumire` (Stocuri.tsx:144) — the
            name alone. A legend sits under the plot and repeats once per
            series; the full identity belongs to the table beneath it. */}
        {splitMedicineLabel(series.label).name}
        {place ? ` · ${place}` : ''}
        {/* UC91-09 projects nothing from nothing, so a medicine nobody reported has
            no line at all; the legend says why instead of leaving it unexplained
            (TC-UC91-09-03). */}
        {series.points.length === 0 ? ` · ${noData}` : ''}
      </span>
    </MudCheckbox>
  );
}
